import { StatusCode } from "api/protocols";
import { GetWebsiteBuilderIconAssets, GetWebsiteBuilderIconProviders } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { IWebsiteBuilderIconAssetIdentity } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderIconAsset, IWebsiteBuilderIconVariant } from "redux/reducers/models/websiteBuilder";
import useDebounce from "hooks/events/useDebounce";

interface IWebsiteBuilderIconPickerQueryState {
  variants: IWebsiteBuilderIconVariant[];
  assets: IWebsiteBuilderIconAsset[];
  variantsLoading: boolean;
  assetsLoading: boolean;
  selectedVariantCode: string;
  assetSearchValue: string;
}

interface IWebsiteBuilderIconPickerState extends IWebsiteBuilderIconPickerQueryState {
  filteredAssets: IWebsiteBuilderIconAsset[];
  renderCount: number;
}

interface IUseWebsiteBuilderIconPicker {
  iconPickerState: IWebsiteBuilderIconPickerState;
  lastIconRef: RefObject<HTMLButtonElement>;
  setSelectedVariantCode: (variantCode: string) => void;
  setAssetSearchValue: (assetSearchValue: string) => void;
}

const PROVIDER_CODE = "phosphor";
const VERSION_CODE = "v1";

const INITIAL_RENDER_COUNT = 200;
const ICON_BATCH_SIZE = 100;
const SCROLL_BOTTOM_THRESHOLD = 200;

export function useWebsiteBuilderIconPicker(assetIdentity: IWebsiteBuilderIconAssetIdentity, isOpen: boolean, scrollContainerSelector: string): IUseWebsiteBuilderIconPicker {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const renderPendingRef = useRef(false);
  const lastIconRef = useRef<HTMLButtonElement>(null);
  const loadVariantsRef = useRef<(token: CancelToken) => Promise<void>>(null);

  const [queryState, setQueryState] = useState<IWebsiteBuilderIconPickerQueryState>({
    variants: [],
    assets: [],
    variantsLoading: false,
    assetsLoading: false,
    selectedVariantCode: null,
    assetSearchValue: "",
  });

  const [renderCount, setRenderCount] = useState(INITIAL_RENDER_COUNT);

  const debouncedAssetSearchValue = useDebounce(queryState.assetSearchValue, 300);

  const filteredAssets = useMemo(() => {
    const searchValue = debouncedAssetSearchValue.trim().toLowerCase();

    return queryState.assets.filter(asset => {
      const isSelected =
        asset.provider_code === assetIdentity?.providerCode &&
        asset.version_code === assetIdentity?.versionCode &&
        asset.variant_code === assetIdentity?.variantCode &&
        asset.asset_code === assetIdentity?.assetCode;

      return !isSelected && asset.title.toLowerCase().includes(searchValue);
    });
  }, [debouncedAssetSearchValue, queryState.assets, assetIdentity]);

  useEffect(() => {
    renderPendingRef.current = false;
  }, [renderCount]);

  useEffect(() => {
    setRenderCount(INITIAL_RENDER_COUNT);
  }, [debouncedAssetSearchValue, queryState.selectedVariantCode, isOpen]);

  useEffect(() => {
    const lastIcon = lastIconRef.current;
    const scrollRoot = lastIcon?.closest(scrollContainerSelector);

    if (
      !isOpen ||
      lastIcon == null ||
      scrollRoot == null ||
      queryState.variantsLoading ||
      queryState.assetsLoading ||
      renderCount >= filteredAssets.length
    ) {
      return;
    }

    function handleScroll() {
      if (renderPendingRef.current) {
        return;
      }

      if (scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight <= SCROLL_BOTTOM_THRESHOLD) {
        renderPendingRef.current = true;
        setRenderCount(prev => prev + ICON_BATCH_SIZE);
      }
    }

    scrollRoot.addEventListener("scroll", handleScroll);

    return () => scrollRoot.removeEventListener("scroll", handleScroll);
  }, [
    scrollContainerSelector,
    isOpen,
    queryState.variantsLoading,
    queryState.assetsLoading,
    renderCount,
    filteredAssets,
  ]);

  useEffect(() => {
    loadVariantsRef.current = async (token: CancelToken) => {
      setQueryState(prev => ({ ...prev, variantsLoading: true }));

      const res = await GetWebsiteBuilderIconProviders(false, token);

      if (res.status !== StatusCode.OK) {
        if (!token.reason) {
          dispatch(showError(typeof res.data === "string" ? res.data : t("Error loading icon variants")));
          setQueryState(prev => ({ ...prev, variantsLoading: false }));
        }

        return;
      }

      const provider = res.data?.find(provider => provider.code === PROVIDER_CODE);
      const version = provider?.versions.find(version => version.code === VERSION_CODE);
      const variants = [...(version?.variants ?? [])].sort((prev, next) => prev.order - next.order);

      const initialVariantCode =
        assetIdentity?.providerCode === PROVIDER_CODE && assetIdentity?.versionCode === VERSION_CODE
          ? assetIdentity.variantCode
          : null;

      const selectedVariant = variants.find(variant => variant.code === initialVariantCode);
      const defaultVariantCode = selectedVariant?.code ?? variants[0]?.code ?? null;

      setQueryState(prev => ({
        ...prev,
        variants,
        variantsLoading: false,
        selectedVariantCode: defaultVariantCode,
      }));
    };
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (isOpen) {
      void loadVariantsRef.current(source.token);
    } else {
      setQueryState(() => ({
        variants: [],
        assets: [],
        variantsLoading: false,
        assetsLoading: false,
        selectedVariantCode: null,
        assetSearchValue: "",
      }));
    }

    return () => {
      if (isOpen) {
        source.cancel();
      }
    };
  }, [isOpen]);

  const loadAssets = useCallback(async (variantCode: string, token: CancelToken) => {
    setQueryState(prev => ({ ...prev, assets: [], assetsLoading: true }));

    const res = await GetWebsiteBuilderIconAssets({
      provider_code: PROVIDER_CODE,
      version_code: VERSION_CODE,
      variant_code: variantCode,
    }, false, token);

    if (res.status !== StatusCode.OK) {
      if (!token.reason) {
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error loading icon assets")));
      }
      
      setQueryState(prev => ({ ...prev, assetsLoading: false }));
      return;
    }

    setQueryState(prev => ({ ...prev, assets: res.data ?? [], assetsLoading: false }));
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();

    const selectedVariantCodeExists = isOpen && queryState.selectedVariantCode != null;

    if (selectedVariantCodeExists) {
      void loadAssets(queryState.selectedVariantCode, source.token);
    }

    return () => {
      if (selectedVariantCodeExists) {
        source.cancel();
      }
    };
  }, [isOpen, queryState.selectedVariantCode, loadAssets]);

  function setSelectedVariantCode(variantCode: string) {
    setQueryState(prev => ({ ...prev, selectedVariantCode: variantCode }));
  }

  function setAssetSearchValue(assetSearchValue: string) {
    setQueryState(prev => ({ ...prev, assetSearchValue }));
  }

  return {
    iconPickerState: {
      ...queryState,
      filteredAssets,
      renderCount,
    },
    lastIconRef,
    setSelectedVariantCode,
    setAssetSearchValue,
  };
}
